import { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Image } from "@themes/pollinator/ui/Image";
import { Type } from "@themes/pollinator/ui/Type";
import { NewsCoverArt } from "../NewsCoverArt";
import { newsType } from "../../news.type";
import type { NewsPost } from "../../news.types";

export type Props = {
  post: NewsPost;
  /** Where the card goes */
  href: string;
};

/**
 * One post in the News grid: its picture, its kind, its title and the start
 * of its summary. The whole card is the link.
 *
 * Every card is the same height, whatever it carries, so the pictures line
 * up from one page of the scroller to the next: a post without a picture of
 * its own gets circles drawn from its slug in the picture's place, the
 * title and summary share six lines, the title one to four and the summary
 * what it leaves, two to three (each cut there if longer), with the
 * summary straight under the title and any room left under it, and the kind
 * keeps its line on a post with none. The full title is on the post's
 * own page.
 */
/** The lines a card's title and summary share, and the fewest and most the summary is given. */
const CARD_LINES = 6;
const SUMMARY_LINES_AT_LEAST = 2;
const SUMMARY_LINES_AT_MOST = 3;

export function NewsCard({ post, href }: Props) {
  // How many lines the title wrapped to, read from its row (25px a line), so
  // the summary can be given the rest. Until it is measured, and where
  // nothing is laid out (as in tests), the title is taken as one line.
  const titleRow = useRef<HTMLDivElement>(null);
  const [titleLines, setTitleLines] = useState(1);
  useLayoutEffect(() => {
    const row = titleRow.current;
    if (!row) return;
    const measure = () => setTitleLines(Math.round(row.getBoundingClientRect().height / 25) || 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, []);
  const summaryLines = Math.min(SUMMARY_LINES_AT_MOST, Math.max(SUMMARY_LINES_AT_LEAST, CARD_LINES - titleLines));

  return (
    <Link
      to={href}
      className="group flex flex-col gap-4 rounded-2xl text-brand-800 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-600"
      data-testid="news-card"
    >
      <div className="aspect-[395/263] w-full overflow-hidden rounded-2xl bg-brand-100" data-testid="news-card-picture">
        {post.cover_image ? (
          <Image
            src={post.cover_image}
            alt=""
            className="h-full w-full"
            imageClassName="object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          />
        ) : (
          <NewsCoverArt
            seed={post.slug}
            className="h-full w-full transition-transform duration-300 group-hover:scale-[1.02]"
          />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <Type variant="caption" as="p" className={newsType.cardLabel}>
          {post.label ?? ""}
        </Type>
        <div className={`flex flex-col gap-1 ${newsType.cardText}`}>
          <div ref={titleRow} className="flex gap-4">
            <Type variant="h4" as="h3" className={`${newsType.cardTitle} min-w-0 flex-1 group-hover:underline`}>
              {post.title}
            </Type>
            <ArrowUpRight aria-hidden className="mt-0.5 size-6 shrink-0 text-brand-700" strokeWidth={2} />
          </div>
          <Type
            variant="body2"
            // line-clamp-3 sets the clamping up; the number of lines is the
            // card's to say, inline, where it outranks the class.
            className={`${newsType.cardExcerpt} line-clamp-3`}
            style={{ WebkitLineClamp: summaryLines }}
            data-testid="news-card-excerpt"
          >
            {post.excerpt ?? ""}
          </Type>
        </div>
      </div>
    </Link>
  );
}

export default NewsCard;
